import { useObserva } from "@morganisbatman/observa-client-react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";

import { fontFamily, space, textStyle } from "@passfirst/ui";

import { BrandSplit } from "../src/brand-split";
import { PinnedCtaFooter } from "../src/components/pinned-cta-footer";
import { type Colors, useColors } from "../src/theme";
import { CredentialRow } from "../src/welcome/credential-row";
import { CredentialSearch } from "../src/welcome/credential-search";
import { groupCredentials } from "../src/welcome/group-credentials";
import { useCredentials } from "../src/welcome/use-credentials";

// Step 1 of onboarding — credential picker. Loads live verticals via
// useCredentials, filters by a search box, groups the matches by industry
// category, and renders each as a CredentialRow (single column on narrow,
// two-up on wide). Routes to /state or /practice depending on
// hasJurisdictions. Emits onboarding.credential_picked on continue.
//
// Deep-link entry: a marketing CTA lands here with ?vertical=<slug>
// (+ optional ?jurisdiction=<code>) — the credential is already chosen, so
// we resolve straight on to the state picker or practice instead of making
// the user re-pick. Only the credential is asked here, so the onboarding is
// 1 step for federal-only exams and 2 (credential + state) for state-bound.
export default function Welcome(): JSX.Element {
  const router = useRouter();
  const observa = useObserva();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const params = useLocalSearchParams<{
    vertical?: string;
    jurisdiction?: string;
  }>();
  const deepVertical =
    typeof params.vertical === "string" ? params.vertical : null;
  const deepJurisdiction =
    typeof params.jurisdiction === "string" ? params.jurisdiction : null;

  const { rows, error } = useCredentials();
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  // While a valid deep-linked credential is being resolved, suppress the
  // picker UI so it doesn't flash before the redirect fires.
  const [resolving, setResolving] = useState<boolean>(deepVertical !== null);

  useEffect(() => {
    if (!deepVertical || !rows) return;
    const row = rows.find((r) => r.slug === deepVertical);
    if (!row) {
      // Unknown slug in the link — fall back to the manual picker.
      setResolving(false);
      return;
    }
    void observa.event({
      eventType: "onboarding.credential_picked",
      payload: { verticalSlug: row.slug },
    });
    if (deepJurisdiction) {
      router.replace({
        pathname: "/practice",
        params: { vertical: row.slug, jurisdiction: deepJurisdiction },
      });
    } else if (row.hasJurisdictions) {
      router.replace({ pathname: "/state", params: { vertical: row.slug } });
    } else {
      router.replace({ pathname: "/practice", params: { vertical: row.slug } });
    }
  }, [deepVertical, deepJurisdiction, rows, router, observa]);

  const selectedRow = useMemo(
    () => rows?.find((r) => r.slug === selected) ?? null,
    [rows, selected],
  );
  // Onboarding length depends on the picked credential: state-bound exams
  // ask credential + state (2); federal-only ask just the credential (1).
  // Default to 2 before a pick so the stepper/copy don't understate.
  const totalSteps = selectedRow && !selectedRow.hasJurisdictions ? 1 : 2;
  const panelTitle =
    totalSteps === 1 ? "Set up in one question." : "Set up in two questions.";

  const groups = useMemo(
    () => (rows ? groupCredentials(rows, query) : []),
    [rows, query],
  );
  const shownCount = useMemo(
    () => groups.reduce((n, g) => n + g.rows.length, 0),
    [groups],
  );

  function handleContinue(): void {
    if (!selected) return;
    const v = rows?.find((x) => x.slug === selected);
    if (!v) return;
    void observa.event({
      eventType: "onboarding.credential_picked",
      payload: { verticalSlug: selected },
    });
    if (v.hasJurisdictions) {
      router.push({ pathname: "/state", params: { vertical: selected } });
    } else {
      router.push({ pathname: "/practice", params: { vertical: selected } });
    }
  }

  if (error && !rows) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.error}>{error}</Text>
      </View>
    );
  }
  if (!rows || resolving) {
    return (
      <View style={styles.errorContainer}>
        <ActivityIndicator color={colors.accentSoftText} />
      </View>
    );
  }

  return (
    <BrandSplit
      panelTitle={panelTitle}
      panelBody="Tell us your credential and we'll scope every question, the tutor, and the voice coach to exactly your exam."
      step={1}
      totalSteps={totalSteps}
      shieldLine="No account needed to start."
      contentWidth={640}
      footer={
        <PinnedCtaFooter
          label="Continue"
          onPress={handleContinue}
          disabled={!selected}
          contentWidth={640}
        />
      }
    >
      {(isWide) => (
        <>
          <Text style={styles.eyebrow}>
            {isWide ? `STEP 1 OF ${totalSteps}` : "WELCOME TO PASSFIRST"}
          </Text>
          {isWide ? (
            // Panel already carries "Set up in two questions." — the pane
            // gets the lighter H3 directive (design_handoff_2 §2 web).
            <Text style={styles.titleWide}>Pick your credential</Text>
          ) : (
            <>
              <Text style={styles.title}>What are you{"\n"}studying for?</Text>
              <Text style={styles.lede}>
                Pick your credential. We&apos;ll scope every question, the
                tutor, and the coach to exactly your exam.
              </Text>
            </>
          )}

          <View style={styles.search}>
            <CredentialSearch value={query} onChange={setQuery} />
          </View>

          {groups.length === 0 ? (
            <View style={styles.empty}>
              <Text style={styles.emptyTitle}>No matches</Text>
              <Text style={styles.emptyBody}>
                Nothing matches “{query.trim()}”. We add credentials every
                month — try a broader term.
              </Text>
            </View>
          ) : (
            groups.map((group) => (
              <View key={group.category} style={styles.group}>
                <Text style={styles.groupLabel}>
                  {group.label.toUpperCase()}
                </Text>
                <View style={styles.grid}>
                  {group.rows.map((v) => (
                    <View
                      key={v.slug}
                      style={[
                        styles.gridItem,
                        isWide ? styles.gridItemWide : styles.gridItemNarrow,
                      ]}
                    >
                      <CredentialRow
                        slug={v.slug}
                        name={v.name}
                        meta={v.meta}
                        selected={selected === v.slug}
                        onPress={() => setSelected(v.slug)}
                      />
                    </View>
                  ))}
                </View>
              </View>
            ))
          )}

          <Text style={styles.footnote}>
            {query.trim()
              ? `${shownCount} of ${rows.length} credentials`
              : `${rows.length} ${rows.length === 1 ? "credential" : "credentials"} live · more each month`}
          </Text>
        </>
      )}
    </BrandSplit>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    errorContainer: {
      flex: 1,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      padding: space[6],
    },
    eyebrow: {
      ...textStyle.eyebrow,
      color: colors.action,
      marginBottom: space[3],
    },
    title: {
      fontFamily: fontFamily.extraBold,
      fontSize: 32,
      lineHeight: 36,
      letterSpacing: -0.5,
      color: colors.heading,
      marginBottom: space[3],
    },
    titleWide: {
      fontFamily: fontFamily.extraBold,
      fontSize: 22,
      lineHeight: 28,
      letterSpacing: -0.4,
      color: colors.heading,
      marginBottom: space[5],
    },
    lede: {
      ...textStyle.body,
      color: colors.textSecondary,
      marginBottom: space[6],
    },
    search: { marginBottom: space[6] },
    group: { marginBottom: space[6] },
    groupLabel: {
      ...textStyle.eyebrow,
      color: colors.textMuted,
      marginBottom: space[3],
    },
    grid: {
      flexDirection: "row",
      flexWrap: "wrap",
      columnGap: 10,
      rowGap: 10,
    },
    gridItem: { flexGrow: 1 },
    // Two-up on wide; minWidth lets a too-narrow pane fall back to one column.
    gridItemWide: { flexBasis: "47%", minWidth: 240 },
    gridItemNarrow: { flexBasis: "100%" },
    empty: {
      paddingVertical: space[8],
      alignItems: "center",
      gap: space[2],
    },
    emptyTitle: {
      fontFamily: fontFamily.bold,
      fontSize: 17,
      color: colors.textPrimary,
    },
    emptyBody: {
      ...textStyle.body,
      color: colors.textSecondary,
      textAlign: "center",
      maxWidth: 340,
    },
    footnote: {
      ...textStyle.caption,
      color: colors.textMuted,
      textAlign: "center",
    },
    error: {
      ...textStyle.body,
      color: colors.error,
      textAlign: "center",
    },
  });
}
